import { createContext, useContext, type FC, type ReactNode } from 'react';
import type { ColorByRule } from 'components/color-by-attribute-modal';

const WellsColorRulesContext = createContext<ColorByRule[]>([]);

/**
 * Publishes a host's colour rules to the surfaces nested inside it.
 *
 * The Explorer paints three targets but is mounted by two hosts that keep their rules
 * in separate atoms, so each host publishes its own here rather than the Explorer
 * knowing which it is under. An absent provider reads as no rules, so its other hosts
 * are unaffected.
 * @param {{ rules: ColorByRule[], children: ReactNode }} props The host's rules.
 * @return {JSX.Element} The provider.
 */
export const WellsColorRulesProvider: FC<{
  rules: ColorByRule[];
  children: ReactNode;
}> = ({ rules, children }) => (
  <WellsColorRulesContext.Provider value={rules}>
    {children}
  </WellsColorRulesContext.Provider>
);

/**
 * The colour rules of the host this surface is mounted under, or none.
 * @return {ColorByRule[]} The rules.
 */
export const useWellsColorRules = (): ColorByRule[] =>
  useContext(WellsColorRulesContext);
